<template>
  <div>
    <div
        class="ui-input-container"
        :class="[classList, sizeClass]"
        @click="$emit('click', $event)"
    >
      <div class="ui-input-container__before">
        <slot name="before"/>
      </div>
      <div
          class="ui-input-container__body"
          :class="sizeClass"
      >
        <div
            class="ui-input-container__title"
            :class="[activeClass, sizeClass]"
            v-if="hasTitle"
        >
          <slot name="title">
            {{ title }}
          </slot>
        </div>
        <div
            v-show="isActive"
            class="ui-input-container__value"
        >
          <slot/>
        </div>
      </div>
      <div class="ui-input-container__after">
        <slot name="after"/>
      </div>
    </div>
    <div
        v-if="errorText && error"
        class="ui-input-container__error--text">
      {{ errorText }}
    </div>
    <div
        v-if="hasDescription"
        class="ui-input-container__description"
        tabindex="-1"
    >
      <slot name="description" />
    </div>
  </div>
</template>

<script>
export default {
  props: {
    title: {
      type: [String, Number],
      default: ''
    },
    hasDescription: {
      type: Boolean,
      default: false
    },
    hasValue: {
      type: Boolean,
      default: false
    },
    focus: {
      type: Boolean,
      default: false
    },
    error: {
      type: Boolean,
      default: false
    },
    errorText: {
      type: String,
      default: ''
    },
    hasTitle: {
      type: Boolean,
      default: true
    },
    hasFilledHighlight: {
      type: Boolean,
      default: false
    },
    small: {
      type: Boolean,
      default: false
    },
    type: {
      type: String,
      default: 'default'
    }
  },
  computed: {
    isActive () {
      return this.focus || this.hasValue
    },
    classList () {
      return [
        `_type_${this.type}`,
        {
          'is-light': this.hasValue && this.hasFilledHighlight,
          error: this.error,
          focus: this.focus,
          'not-title': !this.hasTitle
        }
      ]
    },
    sizeClass () {
      return {
        'size-s': this.small
      }
    },
    activeClass () {
      return {
        active: this.isActive
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.ui-input-container {
  $parent: &;
  display: flex;
  padding: 7px 16px;
  min-height: 54px;
  border: 1px solid $gray-05;
  border-radius: $border-radius-01;
  background-color: $light-01;

  &.size-s {
    min-height: 40px;

    #{$parent}__title {
      margin-bottom: 0;
    }
  }

  &._type {
    &_secondary {
      border-color: $gray-7;
      background-color: $gray-7;
    }
  }

  &.primary {
    border-color: $primary;
  }

  &.is-light {
    border-color: $primary;
  }

  &.error {
    border-color: $red;
  }

  &.skeleton {
    background-color: $gray-7;
    border-color: $gray-7;
  }

  &__body {
    position: relative;
    flex: 1;
    width: 20px;

    &.size-s {
      align-self: center;
    }
  }

  &__title {
    @include font-16;
    @include ellipsis;
    $titleHeight: 20px;
    position: relative;
    top: calc(50% - (#{$titleHeight} / 2));
    height: $titleHeight;
    max-width: 100%;
    margin-bottom: 2px;
    color: $gray-06;
    font-weight: 400;
    user-select: none;
    transition: top .1s, transform .1s, font-size .1s;

    &.active {
      @include font-14;
      height: 16px;
      top: 0;
    }

    &.size-s {
      position: static;

      &.active {
        display: none;
      }
    }
  }

  &__description {
    @include font-14;
    margin-top: 5px;
    line-height: 20px;
    color: $gray-3;
  }

  &__value {
    @include font-16;
    display: flex;
    align-items: center;
    flex-grow: 1;
    color: $black-02;
  }

  &__after,
  &__before {
    @include font-16;
    position: relative;
    display: flex;
  }

  &__error--text {
    @include font-14;

    margin-top: 4px;
    color: $red;
  }
}
</style>
